import React, { useEffect, useRef, useState } from "react";

import CustomDateRangePicker from "../components/CustomDateRangePicker";
import CustomInput from "../components/CustomInput";
import CustomTextArea from "../components/CustomTextArea";
import Layout from "../components/layouts/Layout";
import { Button } from "@nextui-org/button";
import SubTicketForm from "../components/ticketDetail/SubTicketForm";
import AppUtility from "../assets/js/AppUtility";
import {
  CreateTicket,
  GetActivityOptions,
  GetBudgetOptions,
  UpdateProposalTicket,
} from "../services/TicketService";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmSubmitTicketModal from "../components/ConfirmSubmitTicketModal";
import { useDisclosure } from "@nextui-org/modal";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@nextui-org/dropdown";
import { ReactComponent as ChevronDown } from "../assets/icons/chevron-down.svg";
import AppConstant from "../assets/js/AppConstant";
import { createFileFromBlobUrl } from "../services/BlobService";
import TargetTable from "../components/Ticket/TargetTable";
import AppUserProfile, { UserType } from "../assets/js/AppUserProfile";
import Swal from "sweetalert2";
import { TicketBrands } from "../models/TicketBrands";
import dayjs from "dayjs";

const CreateTicketPage = () => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const ticket = location.state;

  const [createSubTicketAble, setCreateSubTicketAble] = useState(true);
  const [errors, setErrors] = useState({});
  const [subTickets, setSubTickets] = useState([]);
  const [submittedSubTickets, setSubmittedSubTickets] = useState([]);
  const [editingSubTicketId, setEditingSubTicketId] = useState(null);
  const [formData, setFormData] = useState({
    project_name: "",
    event_date: "",
    description: "",
    budget_details: [],
    event_start_date: "",
    event_end_date: "",
    activity_name: "",
    activity_custom: "",
  });
  const targetTable = useRef();
  const [carInfos, setCarInfos] = useState([]);

  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [activityOptions, setActivityOptions] = useState([]);
  const [budgetOptions, setBudgetOptions] = useState([]);

  const [selectedActivity, setSelectedActivity] = React.useState(new Set([]));
  const [isEditTicket, setIsEditTicket] = useState(false);
  const userRole = AppUserProfile.getData()["whoami"];

  useEffect(() => {
    const isDealer = () => {
      switch (userRole) {
        case UserType.DEALER_BYD:
        case UserType.DEALER_DENZA:
          return true;
        default:
          return false;
      }
    };

    if (!isDealer()) {
      Swal.fire({
        html: `<h4>คุณไม่มีสิทธิ์ในการเข้าถึง</h4>`,
        icon: "warning",
        confirmButtonText: "ไปหน้าแรก",
      }).then((result) => {
        const currentParams = new URLSearchParams(window.location.search);
        if (result.isConfirmed) {
          navigate(`/?${currentParams.toString()}`);
        } else {
          navigate(`/?${currentParams.toString()}`);
        }
      });
    }
  }, [userRole]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [activityResponse, budgetResponse] = await Promise.all([
          GetActivityOptions(),
          GetBudgetOptions(),
        ]);
        setActivityOptions(activityResponse.data.activity_options);
        setBudgetOptions(budgetResponse.data.budget_options);
        setOptionsLoaded(true);
      } catch (error) {
        console.error("Error loading options");
      }
    };

    loadOptions();
  }, []);

  useEffect(() => {
    if (!optionsLoaded || !ticket) return;

    setIsEditTicket(true);

    setFormData({
      project_name: ticket.project_name,
      description: ticket.description,
      event_date: {
        start: new Date(ticket.event_start_date),
        end: new Date(ticket.event_end_date),
      },
    });
    if (activityOptions.includes(ticket.activity_name)) {
      setSelectedActivity(new Set([ticket.activity_name]));
    } else {
      setSelectedActivity(new Set(["อื่นๆ"]));
      setFormData((prev) => ({
        ...prev,
        activity_custom: ticket.activity_name,
      }));
    }

    if (ticket.sub_tickets && ticket.sub_tickets.length > 0) {
      initialSubTicket();
    }
  }, [optionsLoaded, ticket, activityOptions]);

  const initialSubTicket = async () => {
    const formattedSubTickets = await Promise.all(
      ticket.sub_tickets.map(async (ticket) => {
        const proposalFiles = await Promise.all(
          ticket.proposal_file_urls.map(async (file) => {
            const createdFile = await createFileFromBlobUrl(file);
            return new Map([[createdFile, file]]);
          })
        );

        const imageFiles = proposalFiles.filter((fileMap) => {
          const [[file]] = fileMap.entries();
          return file.type.includes("image");
        });

        const documentFiles = proposalFiles.filter((fileMap) => {
          const [[file]] = fileMap.entries();
          return !file.type.includes("image");
        });

        return {
          id: ticket.id,
          name: ticket.name,
          budget_name: ticket.budget_name,
          description: ticket.description,
          proposal_amount: ticket.proposal_amount,
          proposal_images_urls: imageFiles,
          proposal_document_file_urls: documentFiles,
        };
      })
    );

    setSubmittedSubTickets(formattedSubTickets);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "event_date") {
      const startDayJson = value["start"];
      const endDayJson = value["end"];

      const eventStartDate = AppUtility.formatIsoDate(
        startDayJson.year,
        startDayJson.month,
        startDayJson.day
      );
      const eventEndDate = AppUtility.formatIsoDate(
        endDayJson.year,
        endDayJson.month,
        endDayJson.day
      );

      setFormData({
        ...formData,
        event_date: value,
        event_start_date: eventStartDate,
        event_end_date: eventEndDate,
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    const newErrors = validateForm(formData);
    if (Object.keys(newErrors).length === 0) {
      const processedSubTickets = submittedSubTickets.map((ticket) => {
        const documentUrls = ticket.proposal_document_file_urls.map(
          (fileMap) => {
            const [[, url]] = fileMap.entries();

            return url;
          }
        );
        const imageUrls = ticket.proposal_images_urls.map((fileMap) => {
          const [[, url]] = fileMap.entries();

          return url;
        });

        const { ...extractedTicket } = ticket;
        delete extractedTicket.id;
        delete extractedTicket.proposal_document_file_urls;
        delete extractedTicket.proposal_images_urls;
        return {
          ...extractedTicket,
          proposal_file_urls: [...documentUrls, ...imageUrls],
        };
      });

      const finalFormData = {
        ...formData,
        budget_details: processedSubTickets,
      };

      const createTicketBrand = () => {
        switch (userRole) {
          case UserType.DEALER_BYD:
            return TicketBrands.BYD;
          case UserType.DEALER_DENZA:
            return TicketBrands.Denza;
        }
      };

      try {
        await CreateTicket({
          bodyPayload: {
            ticket: {
              project_name: finalFormData.project_name,
              event_start_date: finalFormData.event_start_date,
              event_end_date: finalFormData.event_end_date,
              description: finalFormData.description,
              is_required_accountant: false,
              activity_name:
                selectedActivityValue == "อื่นๆ"
                  ? finalFormData.activity_custom
                  : selectedActivityValue,
              brand: createTicketBrand(),
            },
            sub_tickets: finalFormData.budget_details,
            ticket_car_infos: carInfos.map((info) => {
              return {
                car_model_name: info.name,
                expected_customer_amount: info.expectedCustomerAmount,
                expected_reserved_amount: info.expectedReservedAmount,
              };
            }),
          },
        });
        onOpenChange();
        const currentParams = new URLSearchParams(window.location.search);
        navigate(`/?${currentParams.toString()}`);
      } catch (error) {
        console.error(error);
      }
    } else {
      console.log("validation errors");
    }
  };

  const validateForm = (data) => {
    const errors = {};
    if (!data.project_name.trim()) {
      errors.project_name = "กรุณาระบุชื่อโปรเจค";
    }

    if (!data.event_date) {
      errors.event_date = "กรุณาระบุระยะเวลาของโปรเจค";
    }

    if (submittedSubTickets.length === 0) {
      errors.budget_details = "กรุณาเพิ่มคำขอย่อยอย่างน้อย 1 คำขอ";
    }

    if (carInfos.length === 0) {
      errors.car_info = "กรุณาเลือกรุ่นรถยนต์อย่างน้อย 1 รุ่น";
    }

    if (targetTable.current.isError()) {
      errors.car_info = "กรุณาใส่จำนวนให้ถูกต้อง";
    }

    if (!selectedActivityValue) {
      errors.activity_name = "กรุณาเลือกลักษณะกิจกรรม";
    }

    if (
      selectedActivityValue === "อื่นๆ" &&
      !formData.activity_custom?.trim()
    ) {
      errors.activity_custom = "กรุณาระบุลักษณะกิจกรรมอื่นๆ";
    }

    setErrors(errors);
    return errors;
  };

  const addSubTicket = () => {
    setCreateSubTicketAble(false);
    const newId = Date.now();
    setSubTickets((prev) => [...prev, { id: newId }]);
  };

  const handleCancelSubTicket = (id) => {
    const isSubmittedTicket = submittedSubTickets.some(
      (ticket) => ticket.id === id
    );

    if (isSubmittedTicket) {
      setSubTickets((prev) => prev.filter((ticket) => ticket.id !== id));
      setEditingSubTicketId(null);
      setCreateSubTicketAble(true);
    } else {
      removeSubTicket(id);
    }
  };

  const removeSubTicket = (id) => {
    setCreateSubTicketAble(true);
    setSubTickets((prev) => prev.filter((ticket) => ticket.id !== id));
    setSubmittedSubTickets((prev) => prev.filter((ticket) => ticket.id !== id));
    if (editingSubTicketId === id) {
      setEditingSubTicketId(null);
    }
  };

  const handleSubTicketSave = async (id, formData) => {
    setCreateSubTicketAble(true);
    setSubmittedSubTickets((data) => {
      const existingIndex = data.findIndex((ticket) => ticket.id === id);
      if (existingIndex !== -1) {
        const updatedTickets = [...data];
        updatedTickets[existingIndex] = { ...formData, id };
        return updatedTickets;
      } else {
        return [...data, { ...formData, id }];
      }
    });
    if (isEditTicket) {
      const imageUrls = formData.proposal_images_urls.map((fileMap) => {
        const [[, url]] = fileMap.entries();
        return url;
      });

      const documentUrls = formData.proposal_document_file_urls.map(
        (fileMap) => {
          const [[, url]] = fileMap.entries();
          return url;
        }
      );

      const { ...rest } = formData;
      delete rest.proposal_images_urls;
      delete rest.proposal_document_file_urls;
      delete rest.budget_name_custom;
      const modifyObject = {
        ...rest,
        proposal_file_urls: [...imageUrls, ...documentUrls],
      };

      const response = await UpdateProposalTicket({
        ticketId: ticket.id,
        bodyPayload: {
          to_update_sub_tickets: Array.of(modifyObject),
        },
      });
      if (response.status === 200) {
        setSubTickets((data) => data.filter((ticket) => ticket.id !== id));
      }
    } else {
      setSubTickets((data) => data.filter((ticket) => ticket.id !== id));
    }
  };

  const handleEditSubTicket = (id) => {
    setEditingSubTicketId(id);
    if (!subTickets.some((ticket) => ticket.id === id)) {
      setSubTickets((prev) => [...prev, { id }]);
    }
    setCreateSubTicketAble(false);
  };

  const allSubTickets = [
    ...submittedSubTickets.filter(
      (st) => !subTickets.some((t) => t.id === st.id)
    ),
    ...subTickets,
  ].sort((a, b) => a.id - b.id);

  const selectedActivityValue = React.useMemo(
    () => Array.from(selectedActivity).join(", ").replaceAll("_", " "),
    [selectedActivity]
  );

  const setModelTargetFromTable = (infos) => {
    setCarInfos(infos);
  };

  return (
    <>
      <Layout removeFilterBar>
        <div>
          <div className="mx-auto my-6 rounded-3xl px-6 pt-6 pb-6 lg:pt-12 lg:px-12 bg-white">
            <div className="flex flex-col gap-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
                <CustomInput
                  label={"ชื่อโปรเจค"}
                  name="project_name"
                  onChange={handleChange}
                  value={formData.project_name}
                  error={errors.project_name}
                  errorMessage={errors.project_name}
                  isDisabled={isEditTicket}
                />
                <div
                  className={` grid grid-cols-1 lg:grid-cols-${
                    selectedActivityValue === "อื่นๆ" ? "3" : "1"
                  } gap-6 w-full`}
                >
                  <div className="flex flex-col justify-between">
                    <p className="text-sm pb-2 font-medium ">ลักษณะกิจกรรม</p>

                    <Dropdown>
                      <DropdownTrigger
                        classNames={{ content: "min-w-[--trigger-width]" }}
                      >
                        <Button
                          variant="bordered"
                          endContent={<ChevronDown width={10} height={10} />}
                          className={`w-full flex justify-between items-center ${
                            (errors.activity_name || errors.activity_custom) &&
                            "border-red-400"
                          }`}
                          isDisabled={isEditTicket}
                        >
                          {selectedActivityValue.length
                            ? selectedActivityValue
                            : "กรุณาเลือก"}
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        disallowEmptySelection={false}
                        closeOnSelect={false}
                        selectedKeys={selectedActivity}
                        selectionMode="single"
                        variant="flat"
                        onSelectionChange={setSelectedActivity}
                      >
                        {activityOptions.map((activity) => (
                          <DropdownItem key={activity}>{activity}</DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                    <p className="text-red-500 text-sm ">
                      {errors.activity_name ?? errors.activity_custom}
                    </p>
                  </div>
                  {selectedActivityValue === "อื่นๆ" && (
                    <div className="col-span-2">
                      <CustomInput
                        label={" "}
                        name="activity_custom"
                        onChange={handleChange}
                        value={formData.activity_custom}
                        error={errors.activity_custom}
                        errorMessage={errors.activity_custom}
                        className=""
                        isDisabled={isEditTicket}
                      />
                    </div>
                  )}
                </div>
                <CustomDateRangePicker
                  label={"ระยะเวลาจัดงาน"}
                  name="event_date"
                  variant={"underlined"}
                  onChange={handleChange}
                  value={formData.event_date}
                  error={errors.event_date ? true : false}
                  errorMessage={errors.event_date}
                  isDisabled={isEditTicket}
                  minDate={dayjs().add(3, "days")}
                />
              </div>
              <CustomTextArea
                label={"รายละเอียด"}
                lableOpacity={100}
                name="description"
                onChange={handleChange}
                value={formData.description}
                error={errors.description ? true : false}
                errorMessage={errors.description}
                isDisabled={isEditTicket}
              />

              <div className="flex flex-col gap-y-2">
                <TargetTable
                  ref={targetTable}
                  isCreate
                  submitData={setModelTargetFromTable}
                />
                <div className="text-center text-red-500 text-xs">
                  {errors.car_info}
                </div>
              </div>
              <div className="pt-4 text-lg font-semibold">คำขอย่อย</div>
              <div className="flex flex-col">
                <div
                  className={`${
                    errors.budget_details
                      ? "border-2 border-red-500"
                      : "border-none"
                  } bg-light-gray-600 p-6  mb-2 rounded-3xl flex flex-col gap-y-4`}
                >
                  {allSubTickets.map((ticket) => {
                    const submittedData = submittedSubTickets.find(
                      (st) => st.id === ticket.id
                    );
                    if (
                      submittedData &&
                      !subTickets.some((t) => t.id === ticket.id)
                    ) {
                      return (
                        <div
                          key={ticket.id}
                          className="bg-white rounded-3xl px-8 py-12"
                        >
                          <div className="flex flex-col gap-y-5">
                            <div className="flex justify-between items-center mb-4">
                              <h3 className="text-lg font-medium">
                                {submittedData.name}
                              </h3>
                              <Button
                                className="bg-gray-200 text-black"
                                size="sm"
                                onPress={() => {
                                  handleEditSubTicket(ticket.id);
                                }}
                              >
                                แก้ไข
                              </Button>
                            </div>
                            <div className="flex flex-col gap-4">
                              <div>
                                <p className="text-gray-500 mb-1">
                                  ลักษณะค่าใช้จ่าย
                                </p>
                                <p>{submittedData.budget_name}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 mb-1">จำนวน</p>
                                <p>฿{submittedData.proposal_amount}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 mb-1">รายละเอียด</p>
                                <p>{submittedData.description}</p>
                              </div>
                            </div>
                            {(submittedData.proposal_images_urls?.length > 0 ||
                              submittedData.proposal_document_file_urls
                                ?.length > 0) && (
                              <div>
                                <p className="text-gray-500 mb-2">ไฟล์แนบ</p>
                                <div className="flex flex-row flex-wrap gap-x-4 pt-2 gap-y-4">
                                  {submittedData.proposal_images_urls.map(
                                    (fileMap, idx) => {
                                      const [[file, fileName]] =
                                        fileMap.entries();
                                      return (
                                        <React.Fragment key={file.name}>
                                          <img
                                            key={idx}
                                            src={`${AppConstant.STOARGE_ACCOUNT}/ticket-files/${fileName}?${AppConstant.STORAGE_TOKEN}`}
                                            className="w-32 h-32 object-cover rounded-md"
                                            alt="Uploaded file"
                                          />
                                        </React.Fragment>
                                      );
                                    }
                                  )}
                                  {submittedData.proposal_document_file_urls.map(
                                    (fileMap) => {
                                      const [[file, url]] = fileMap.entries();
                                      return (
                                        <React.Fragment key={url}>
                                          <div className="h-32 w-32 rounded-xl shrink-0 border-light-gray-300 border-3 bg-white flex items-center justify-center px-5 text-center">
                                            <p className="line-clamp-4 break-words text-sm">
                                              {file.name}
                                            </p>
                                          </div>
                                        </React.Fragment>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <SubTicketForm
                          key={ticket.id}
                          onCancel={() => handleCancelSubTicket(ticket.id)}
                          onSave={(isSave, formData) => {
                            if (isSave) {
                              handleSubTicketSave(ticket.id, formData);
                            }
                          }}
                          initialData={submittedSubTickets.find(
                            (st) => st.id === ticket.id
                          )}
                          isEditing={editingSubTicketId === ticket.id}
                          isSubmitted={submittedSubTickets.some(
                            (st) => st.id === ticket.id
                          )}
                          budgetOptions={budgetOptions}
                        />
                      );
                    }
                  })}

                  <Button
                    className="bg-complete text-white w-full text-md mt-5"
                    radius="sm"
                    startContent={<div className="text-md">+</div>}
                    onPress={addSubTicket}
                    isDisabled={!createSubTicketAble}
                  >
                    เพิ่มหมวดหมู่ค่าใช้จ่าย
                  </Button>
                </div>
                <p className="text-red-500 text-sm">{errors.budget_details}</p>
              </div>
              {!isEditTicket && (
                <div className="flex flex-row w-full gap-x-4 justify-end">
                  <Button
                    className="bg-blue-600 text-white w-fit text-md mt-5"
                    radius="sm"
                    onPress={() => {
                      let errors = validateForm(formData);
                      if (Object.keys(errors).length === 0) {
                        onOpen();
                      }
                    }}
                  >
                    ยื่นคำขอ
                  </Button>
                  <Button
                    className="bg-light-gray-300 text-black w-fit text-md mt-5"
                    radius="sm"
                    onPress={() => {
                      const currentParams = new URLSearchParams(
                        window.location.search
                      );
                      navigate(`/?${currentParams.toString()}`);
                    }}
                  >
                    ยกเลิก
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
      <ConfirmSubmitTicketModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onConfirm={(e) => handleSubmit(e)}
      />
    </>
  );
};

export default CreateTicketPage;
